import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, ProgressBar, Row } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";

import { getImageUrl } from "../../../utils/imageUrl";
import { listPlayers } from "../../../api/tournament/playerService";
import {
  ExistingPlayerHighlight,
  ExistingTeamHighlight,
  createHighlightAssetSequence,
  getHighlightAssetSequence,
  getMatchHighlights,
  HighlightAccessType,
  updateMatchHighlights,
  uploadHighlightAsset,
} from "../../../api/tournament/matchHighlightsService";

type DraftState = "idle" | "uploading" | "uploaded_not_published" | "publishing" | "published" | "failed";

interface EntityOption {
  value: string;
  label: string;
  image?: string | null;
}

interface HighlightRow {
  id: string;
  entityId: string;
  highlights: string;
  thumbnail?: string | null;
  assetSequence: string;
  state: DraftState;
  progress: number;
  thumbnailState?: DraftState;
  thumbnailProgress?: number;
  error?: string | null;
  thumbnailError?: string | null;
}

interface MatchHighlightsSectionProps {
  matchId: string;
  sport: string;
  tournamentId?: string;
  tournamentSeasonId?: string;
  fixture: any;
  liveScoreData?: any;
}

const emptyMatchHighlight = {
  default: null as string | null,
  thumbnail: null as string | null,
  uploadedDate: null as string | null,
  accessType: "free" as HighlightAccessType,
};

const entityId = (value: string | { _id: string }): string =>
  typeof value === "string" ? value : value?._id || "";

const uniqueByValue = (items: EntityOption[]) => {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (!item.value || seen.has(item.value)) return false;
    seen.add(item.value);
    return true;
  });
};

const makeRowId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const assetName = (key: string | null | undefined) => {
  if (!key) return "No file selected";
  return key.split("/").pop() || key;
};

const fileIsVideo = (file: File) => file.type.startsWith("video/");
const fileIsImage = (file: File) => file.type.startsWith("image/");
const asArray = <T,>(value: T[] | unknown): T[] => (Array.isArray(value) ? value : []);

const selectMenuStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
  menu: (base: any) => ({ ...base, zIndex: 99999 }),
};

const playerOptionFromApi = (player: any): EntityOption | null => {
  if (!player) return null;
  const info = player.playerInfo || player;
  const linkedPlayer =
    player.player && typeof player.player === "object"
      ? player.player._id || player.player.id
      : player.player;
  const id = info._id || linkedPlayer || player._id || player.id;
  const label = info.name || player.name || player.fullName || player.playerName || player.uniqueId || id;
  if (!id || !label || typeof id !== "string") return null;
  return {
    value: id,
    label,
    image: info.profilePic || player.profilePic || null,
  };
};

const getAgeCategoryFilter = (fixture: any): string => {
  const ageCategory = fixture?.ageCategory;
  if (!ageCategory) return "";
  if (typeof ageCategory === "string") return "";
  return ageCategory.ageCategory || ageCategory.name || ageCategory.label || "";
};

const MatchHighlightsSection: React.FC<MatchHighlightsSectionProps> = ({
  matchId,
  sport,
  tournamentId,
  tournamentSeasonId,
  fixture,
  liveScoreData,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matchHighlight, setMatchHighlight] = useState(emptyMatchHighlight);
  const [matchState, setMatchState] = useState<DraftState>("idle");
  const [matchProgress, setMatchProgress] = useState(0);
  const [thumbnailState, setThumbnailState] = useState<DraftState>("idle");
  const [thumbnailProgress, setThumbnailProgress] = useState(0);
  const [matchAssetSequence, setMatchAssetSequence] = useState("");
  const [playerRows, setPlayerRows] = useState<HighlightRow[]>([]);
  const [teamRows, setTeamRows] = useState<HighlightRow[]>([]);
  const [apiPlayerOptions, setApiPlayerOptions] = useState<EntityOption[]>([]);
  const [playersLoading, setPlayersLoading] = useState(false);

  const playerOptions = useMemo(() => {
    const players: EntityOption[] = [...apiPlayerOptions];
    const pushPlayer = (player: any) => {
      if (!player) return;
      const id = typeof player === "string" ? player : player._id || player.id;
      const label =
        typeof player === "string"
          ? player
          : player.name || player.fullName || player.playerName || player.uniqueId || id;
      if (id && label) players.push({ value: id, label, image: player.profilePic || null });
    };

    asArray(liveScoreData?.playerList).forEach(pushPlayer);
    asArray(fixture?.participants).forEach((participant: any) => asArray(participant?.players).forEach(pushPlayer));
    asArray(fixture?.side1?.players).forEach(pushPlayer);
    asArray(fixture?.side2?.players).forEach(pushPlayer);
    asArray(fixture?.team1Info?.players).forEach(pushPlayer);
    asArray(fixture?.team2Info?.players).forEach(pushPlayer);

    return uniqueByValue(players);
  }, [apiPlayerOptions, fixture, liveScoreData]);

  const teamOptions = useMemo(() => {
    const teams: EntityOption[] = [];
    const pushTeam = (team: any) => {
      if (!team || typeof team === "string") return;
      const id = team._id || team.id;
      const label = team.name || team.shortName || id;
      if (id && label) teams.push({ value: id, label, image: team.logo || null });
    };

    [
      fixture?.teamA,
      fixture?.teamB,
      fixture?.team1Info,
      fixture?.team2Info,
      fixture?.side1?.team,
      fixture?.side2?.team,
      liveScoreData?.teamAInfo,
      liveScoreData?.teamBInfo,
      liveScoreData?.team1Info,
      liveScoreData?.team2Info,
    ].forEach(pushTeam);
    asArray(fixture?.participants).forEach((participant: any) => pushTeam(participant?.team));

    return uniqueByValue(teams);
  }, [fixture, liveScoreData]);

  const loadHighlights = useCallback(async () => {
    if (!matchId || !sport) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getMatchHighlights(matchId, sport);
      setMatchHighlight({
        default: data.matchHighlights?.default || null,
        thumbnail: data.matchHighlights?.thumbnail || null,
        uploadedDate: data.matchHighlights?.uploadedDate || null,
        accessType: data.matchHighlights?.accessType || "free",
      });
      setPlayerRows(
        (data.playerHighlights || []).map((item: ExistingPlayerHighlight) => ({
          id: makeRowId(),
          entityId: entityId(item.player),
          highlights: item.highlights,
          thumbnail: item.thumbnail || null,
        assetSequence: getHighlightAssetSequence(item.highlights) || getHighlightAssetSequence(item.thumbnail),
          state: "published",
          progress: 100,
          thumbnailState: item.thumbnail ? "published" : "idle",
          thumbnailProgress: item.thumbnail ? 100 : 0,
          error: null,
          thumbnailError: null,
        }))
      );
      setTeamRows(
        (data.teamHighlights || []).map((item: ExistingTeamHighlight) => ({
          id: makeRowId(),
          entityId: entityId(item.team),
          highlights: item.highlights,
          thumbnail: item.thumbnail || null,
        assetSequence: getHighlightAssetSequence(item.highlights) || getHighlightAssetSequence(item.thumbnail),
          state: "published",
          progress: 100,
          thumbnailState: item.thumbnail ? "published" : "idle",
          thumbnailProgress: item.thumbnail ? 100 : 0,
          error: null,
          thumbnailError: null,
        }))
      );
      setMatchState(data.matchHighlights?.default ? "published" : "idle");
      setThumbnailState(data.matchHighlights?.thumbnail ? "published" : "idle");
      setMatchProgress(data.matchHighlights?.default ? 100 : 0);
      setThumbnailProgress(data.matchHighlights?.thumbnail ? 100 : 0);
      setMatchAssetSequence(
        getHighlightAssetSequence(data.matchHighlights?.default)
        || getHighlightAssetSequence(data.matchHighlights?.thumbnail)
      );
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to load match highlights.");
    } finally {
      setLoading(false);
    }
  }, [matchId, sport]);

  useEffect(() => {
    loadHighlights();
  }, [loadHighlights]);

  useEffect(() => {
    if (!tournamentId || !sport) {
      setApiPlayerOptions([]);
      return;
    }

    let cancelled = false;
    setPlayersLoading(true);
    listPlayers({
      tournament: tournamentId,
      tournamentSeason: tournamentSeasonId,
      sport,
      matchId,
      ageCategory: getAgeCategoryFilter(fixture),
      page: 1,
      pageSize: 500,
    })
      .then((data) => {
        if (cancelled) return;
        const players = asArray<any>(data?.paginatedResults)
          .map(playerOptionFromApi)
          .filter((option): option is EntityOption => !!option);
        setApiPlayerOptions(uniqueByValue(players));
      })
      .catch(() => {
        if (!cancelled) setApiPlayerOptions([]);
      })
      .finally(() => {
        if (!cancelled) setPlayersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fixture, sport, tournamentId, tournamentSeasonId]);

  const uploadMatchVideo = async (file: File) => {
    if (!fileIsVideo(file)) {
      setError("Please select a video file for the match highlight.");
      return;
    }
    if (!tournamentId || !tournamentSeasonId) {
      setError("Tournament and season are required to upload a match highlight.");
      return;
    }
    const sequence = matchAssetSequence || createHighlightAssetSequence();
    setMatchAssetSequence(sequence);
    setError(null);
    setMatchState("uploading");
    setMatchProgress(0);
    try {
      const key = await uploadHighlightAsset({
        file,
        tournamentId,
        seasonId: tournamentSeasonId,
        matchId,
        sequence,
        kind: "match",
        asset: "video",
      }, setMatchProgress);
      setMatchHighlight((current) => ({
        ...current,
        default: key,
        uploadedDate: new Date().toISOString(),
      }));
      setMatchState("uploaded_not_published");
    } catch (err: any) {
      setMatchState("failed");
      setError(err?.message || "Failed to upload match highlight.");
    }
  };

  const uploadThumbnail = async (file: File) => {
    if (!fileIsImage(file)) {
      setError("Please select an image file for the thumbnail.");
      return;
    }
    if (!tournamentId || !tournamentSeasonId) {
      setError("Tournament and season are required to upload a match highlight.");
      return;
    }
    const sequence = matchAssetSequence || createHighlightAssetSequence();
    setMatchAssetSequence(sequence);
    setError(null);
    setThumbnailState("uploading");
    setThumbnailProgress(0);
    try {
      const key = await uploadHighlightAsset({
        file,
        tournamentId,
        seasonId: tournamentSeasonId,
        matchId,
        sequence,
        kind: "match",
        asset: "thumbnail",
      }, setThumbnailProgress);
      setMatchHighlight((current) => ({ ...current, thumbnail: key }));
      setThumbnailState("uploaded_not_published");
    } catch (err: any) {
      setThumbnailState("failed");
      setError(err?.message || "Failed to upload thumbnail.");
    }
  };

  const uploadRowVideo = async (
    file: File,
    rowId: string,
    type: "player" | "team"
  ) => {
    if (!fileIsVideo(file)) {
      setError("Please select a video file.");
      return;
    }
    if (!tournamentId || !tournamentSeasonId) {
      setError("Tournament and season are required to upload a highlight.");
      return;
    }
    const setRows = type === "player" ? setPlayerRows : setTeamRows;
    const row = (type === "player" ? playerRows : teamRows).find((item) => item.id === rowId);
    const sequence = row?.assetSequence || createHighlightAssetSequence();
    setError(null);
    setRows((rows) =>
      rows.map((row) =>
        row.id === rowId ? { ...row, assetSequence: sequence, state: "uploading", progress: 0, error: null } : row
      )
    );
    try {
      const key = await uploadHighlightAsset({
        file,
        tournamentId,
        seasonId: tournamentSeasonId,
        matchId,
        sequence,
        kind: type,
        asset: "video",
      }, (progress) => {
        setRows((rows) =>
          rows.map((row) => (row.id === rowId ? { ...row, progress } : row))
        );
      });
      setRows((rows) =>
        rows.map((row) =>
          row.id === rowId
            ? { ...row, highlights: key, state: "uploaded_not_published", progress: 100 }
            : row
        )
      );
    } catch (err: any) {
      setRows((rows) =>
        rows.map((row) =>
          row.id === rowId
            ? { ...row, state: "failed", error: err?.message || "Upload failed." }
            : row
        )
      );
    }
  };

  const uploadRowThumbnail = async (file: File, rowId: string, type: "player" | "team") => {
    if (!fileIsImage(file)) {
      setError(`Please select an image file for the ${type} highlight thumbnail.`);
      return;
    }
    if (!tournamentId || !tournamentSeasonId) {
      setError("Tournament and season are required to upload a highlight.");
      return;
    }
    const setRows = type === "player" ? setPlayerRows : setTeamRows;
    const row = (type === "player" ? playerRows : teamRows).find((item) => item.id === rowId);
    const sequence = row?.assetSequence || createHighlightAssetSequence();
    setError(null);
    setRows((rows) =>
      rows.map((row) =>
        row.id === rowId
          ? { ...row, assetSequence: sequence, thumbnailState: "uploading", thumbnailProgress: 0, thumbnailError: null }
          : row
      )
    );
    try {
      const key = await uploadHighlightAsset({
        file,
        tournamentId,
        seasonId: tournamentSeasonId,
        matchId,
        sequence,
        kind: type,
        asset: "thumbnail",
      }, (progress) => {
        setRows((rows) =>
          rows.map((row) =>
            row.id === rowId ? { ...row, thumbnailProgress: progress } : row
          )
        );
      });
      setRows((rows) =>
        rows.map((row) =>
          row.id === rowId
            ? {
                ...row,
                thumbnail: key,
                thumbnailState: "uploaded_not_published",
                thumbnailProgress: 100,
              }
            : row
        )
      );
    } catch (err: any) {
      setRows((rows) =>
        rows.map((row) =>
          row.id === rowId
            ? {
                ...row,
                thumbnailState: "failed",
                thumbnailError: err?.message || "Thumbnail upload failed.",
              }
            : row
        )
      );
    }
  };

  const addRow = (type: "player" | "team") => {
    const row: HighlightRow = {
      id: makeRowId(),
      entityId: "",
      highlights: "",
      thumbnail: null,
      assetSequence: "",
      state: "idle",
      progress: 0,
      thumbnailState: "idle",
      thumbnailProgress: 0,
      error: null,
      thumbnailError: null,
    };
    if (type === "player") setPlayerRows((rows) => [...rows, row]);
    else setTeamRows((rows) => [...rows, row]);
  };

  const updateRowEntity = (type: "player" | "team", rowId: string, entityIdValue: string) => {
    const setRows = type === "player" ? setPlayerRows : setTeamRows;
    setRows((rows) =>
      rows.map((row) => (row.id === rowId ? { ...row, entityId: entityIdValue } : row))
    );
  };

  const validateRows = () => {
    const teamIds = teamRows.map((row) => row.entityId).filter(Boolean);
    if (playerRows.some((row) => !row.entityId || !row.highlights)) {
      return "Each player highlight needs a player and video.";
    }
    if (teamRows.some((row) => !row.entityId || !row.highlights)) {
      return "Each team highlight needs a team and video.";
    }
    if (new Set(teamIds).size !== teamIds.length) {
      return "Duplicate team highlights are not allowed.";
    }
    return null;
  };

  const saveHighlights = async () => {
    const validationError = validateRows();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);
    setMatchState((current) => (current === "uploaded_not_published" ? "publishing" : current));
    try {
      const matchHighlightsPayload = matchHighlight.default
        ? {
            default: matchHighlight.default,
            thumbnail: matchHighlight.thumbnail,
            uploadedDate: matchHighlight.uploadedDate || new Date().toISOString(),
            accessType: matchHighlight.accessType,
          }
        : {
            default: null,
            thumbnail: null,
            uploadedDate: null,
          };

      await updateMatchHighlights(matchId, sport, {
        matchHighlights: matchHighlightsPayload,
        playerHighlights: playerRows.map((row) => ({
          player: row.entityId,
          highlights: row.highlights,
          ...(row.thumbnail ? { thumbnail: row.thumbnail } : {}),
        })),
        teamHighlights: teamRows.map((row) => ({
          team: row.entityId,
          highlights: row.highlights,
          ...(row.thumbnail ? { thumbnail: row.thumbnail } : {}),
        })),
      });
      toast.success("Highlights saved successfully.");
      await loadHighlights();
    } catch (err: any) {
      setMatchState("failed");
      setError(err?.response?.data?.message || err?.message || "Failed to save highlights.");
    } finally {
      setSaving(false);
    }
  };

  const statusBadge = (state: DraftState) => {
    if (state === "uploading") return <Badge bg="info">Uploading</Badge>;
    if (state === "uploaded_not_published") return <Badge bg="warning">Uploaded, not published</Badge>;
    if (state === "publishing") return <Badge bg="primary">Publishing</Badge>;
    if (state === "published") return <Badge bg="success">Published</Badge>;
    if (state === "failed") return <Badge bg="danger">Failed</Badge>;
    return <Badge bg="secondary">Not added</Badge>;
  };

  const renderAssetLink = (key: string | null | undefined) =>
    key ? (
      <a href={getImageUrl(key)} target="_blank" rel="noreferrer">
        {assetName(key)}
      </a>
    ) : (
      <span className="text-muted">No file</span>
    );

  const renderRows = (
    type: "player" | "team",
    rows: HighlightRow[],
    options: EntityOption[]
  ) => (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">{type === "player" ? "Player Highlights" : "Team Highlights"}</h6>
        <Button variant="outline-primary" size="sm" onClick={() => addRow(type)}>
          <i className="ri-add-line me-1" />
          Add {type === "player" ? "Player" : "Team"} Highlight
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="text-muted py-3">No {type} highlights added.</div>
      ) : (
        rows.map((row, index) => (
          <Card
            key={row.id}
            className="mb-3"
            style={{
              border: "1px solid #e6ebf2",
              borderRadius: 10,
              boxShadow: "0 6px 18px rgba(15, 23, 42, 0.04)",
            }}
          >
            <Card.Body>
              <div
                style={{
                  alignItems: "center",
                  borderBottom: "1px solid #edf1f6",
                  display: "flex",
                  justifyContent: "space-between",
                  margin: "-4px 0 16px",
                  paddingBottom: 12,
                }}
              >
                <span style={{ color: "#6b7280", fontSize: 13, fontWeight: 600 }}>
                  Row {index + 1}
                </span>
                {statusBadge(row.state)}
              </div>

              <div
                style={{
                  alignItems: "start",
                  display: "grid",
                  gap: 18,
                  gridTemplateColumns:
                    "minmax(220px, 0.95fr) minmax(280px, 1fr) minmax(280px, 1fr)",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <Form.Label style={{ color: "#343a40", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                    {type === "player" ? "Player" : "Team"}
                  </Form.Label>
                  <Select
                    classNamePrefix="react-select"
                    value={options.find((option) => option.value === row.entityId) || null}
                    onChange={(option) => updateRowEntity(type, row.id, option?.value || "")}
                    options={options}
                    placeholder={`Select ${type}`}
                    isClearable
                    isLoading={type === "player" && playersLoading}
                    menuPlacement="top"
                    menuPosition="fixed"
                    menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                    maxMenuHeight={220}
                    styles={selectMenuStyles}
                  />
                </div>

                <div style={{ minWidth: 0 }}>
                  <Form.Label style={{ color: "#343a40", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                    Video
                  </Form.Label>
                  <Form.Control
                    type="file"
                    accept="video/*"
                    style={{ minHeight: 40, width: "100%" }}
                    onChange={(event) => {
                      const input = event.currentTarget as HTMLInputElement;
                      const file = input.files?.[0];
                      if (file) uploadRowVideo(file, row.id, type);
                      input.value = "";
                    }}
                  />
                  <div
                    style={{
                      color: "#6b7280",
                      fontSize: 12,
                      lineHeight: "18px",
                      marginTop: 8,
                      minHeight: 18,
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    {renderAssetLink(row.highlights)}
                  </div>
                  {row.state === "uploading" && <ProgressBar now={row.progress} label={`${row.progress}%`} className="mt-2" />}
                  {row.error && <div className="text-danger small mt-2">{row.error}</div>}
                </div>

                <div style={{ minWidth: 0 }}>
                  <Form.Label style={{ color: "#343a40", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                    Thumbnail
                  </Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    style={{ minHeight: 40, width: "100%" }}
                    onChange={(event) => {
                      const input = event.currentTarget as HTMLInputElement;
                      const file = input.files?.[0];
                      if (file) uploadRowThumbnail(file, row.id, type);
                      input.value = "";
                    }}
                  />
                  <div
                    style={{
                      color: "#6b7280",
                      fontSize: 12,
                      lineHeight: "18px",
                      marginTop: 8,
                      minHeight: 18,
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    {renderAssetLink(row.thumbnail)}
                  </div>
                  {row.thumbnailState === "uploading" && (
                    <ProgressBar now={row.thumbnailProgress || 0} label={`${row.thumbnailProgress || 0}%`} className="mt-2" />
                  )}
                  {row.thumbnailError && <div className="text-danger small mt-2">{row.thumbnailError}</div>}
                </div>
              </div>
            </Card.Body>
          </Card>
        ))
      )}
    </div>
  );

  return (
    <Card className="mb-4" style={{ border: "1px solid #e0e0e0", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
      <Card.Header className="d-flex justify-content-between align-items-center" style={{ backgroundColor: "#f8f9fc", borderBottom: "1px solid #e0e0e0" }}>
        <h5 className="mb-0" style={{ fontWeight: 600 }}>Highlights</h5>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" size="sm" onClick={loadHighlights} disabled={loading || saving}>
            Refresh
          </Button>
          <Button variant="primary" size="sm" onClick={saveHighlights} disabled={loading || saving || matchState === "uploading" || thumbnailState === "uploading"}>
            {saving ? "Saving..." : "Save Highlights"}
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center py-4">Loading highlights...</div>
        ) : (
          <>
            <Card className="mb-4" style={{ border: "1px solid #e9ecef" }}>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h6 className="mb-1">Match Highlight</h6>
                    <div className="text-muted small">One primary clip for this match.</div>
                  </div>
                  {statusBadge(matchState)}
                </div>
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Label>Highlight Video</Form.Label>
                    <Form.Control
                      type="file"
                      accept="video/*"
                      onChange={(event) => {
                        const input = event.currentTarget as HTMLInputElement;
                        const file = input.files?.[0];
                        if (file) uploadMatchVideo(file);
                        input.value = "";
                      }}
                    />
                    <div className="small mt-2">{renderAssetLink(matchHighlight.default)}</div>
                    {matchState === "uploading" && <ProgressBar now={matchProgress} label={`${matchProgress}%`} className="mt-2" />}
                  </Col>
                  <Col md={4}>
                    <Form.Label>Thumbnail</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(event) => {
                        const input = event.currentTarget as HTMLInputElement;
                        const file = input.files?.[0];
                        if (file) uploadThumbnail(file);
                        input.value = "";
                      }}
                    />
                    <div className="small mt-2">{renderAssetLink(matchHighlight.thumbnail)}</div>
                    {thumbnailState === "uploading" && <ProgressBar now={thumbnailProgress} label={`${thumbnailProgress}%`} className="mt-2" />}
                  </Col>
                  <Col md={4} className="d-flex align-items-end justify-content-end">
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {renderRows("player", playerRows, playerOptions)}
            <hr />
            {renderRows("team", teamRows, teamOptions)}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default MatchHighlightsSection;
